import { useState } from 'react';
import { motion } from "framer-motion";
import {
    FiStar,
    FiShoppingCart,
    FiHeart,
    FiPlus,
    FiMinus,
} from "react-icons/fi";

// ✅ Helper: create URL-friendly slug from product name
const createSlug = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/(^-|-$)/g, '');
};

// ✅ Helper: remove timestamp suffix from API slug (e.g. -1770373161491)
const cleanApiSlug = (slug) => {
    if (!slug) return '';
    return slug.replace(/-\d{10,}$/, '');
};

// ✅ Helper: get best slug for a product
const getProductSlug = (product) => {
    if (product.slug) return cleanApiSlug(product.slug);
    return createSlug(product.name);
};

const ProductCard = ({
    product,
    onAddToCart,
    onToggleWishlist,
    onQuantityUpdate,
    isWishlisted,
    cartQuantity = 0,
    navigate,
    viewMode
}) => {
    const [imageError, setImageError] = useState(false);
    const currentPrice = Number(product.price || product.newPrice || 0);
    const originalPrice = Number(product.originalPrice || product.oldPrice || 0);

    const savings =
        originalPrice > currentPrice
            ? Math.ceil(originalPrice - currentPrice)
            : 0;

    const handleAddClick = (e) => {
        e.stopPropagation();
        onAddToCart(product, 1);
    };

    const handleIncrement = (e) => {
        e.stopPropagation();
        onQuantityUpdate(product.id, 1);
    };

    const handleDecrement = (e) => {
        e.stopPropagation();
        onQuantityUpdate(product.id, -1);
    };

    const handleWishlistClick = (e) => {
        e.stopPropagation();
        onToggleWishlist(product);
    };

    
    // const handleProductClick = () => {
    //     const slug = getProductSlug(product);
    //     navigate(`/product/${slug}`);
    // };

  const handleProductClick = () => {
    const productSlug = getProductSlug(product);

    const categorySlug = product.category
        ?.toLowerCase()
        .replace(/\s+/g, "-");

    navigate(`/${categorySlug}/${productSlug}`);
};

    const isOutOfStock = product.stock === 0;
    const isLowStock = product.stock > 0 && product.stock < 10;

    // Parse size from backend - could be string or array
    const getProductSizes = () => {
        if (!product.size) return [];

        if (typeof product.size === 'string') {
            const sizes = product.size.split(',').map(s => s.trim()).filter(s => s);
            return sizes.slice(0, 3);
        }

        if (Array.isArray(product.size)) {
            return product.size.slice(0, 3);
        }

        return [];
    };

    const productSizes = getProductSizes();

    // LIST VIEW (desktop only)
    if (viewMode === "list") {
        return (
            <motion.div
                whileHover={{ y: -2 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
                <div className="flex flex-row">
                    {/* SQUARE IMAGE CONTAINER (List View) */}
                    <div
                        className="w-32 h-32 aspect-square bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0 cursor-pointer p-2 relative"
                        onClick={handleProductClick}
                    >
                        <img
                            src={imageError ? "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400" : product.image}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            onError={() => setImageError(true)}
                        />

                        {product.discount > 0 && (
                            <div className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-red-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                {product.discount}% OFF
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 p-3">
                        <div className="flex flex-row items-start justify-between h-full">
                            <div className="flex-1 pr-3">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="flex items-center">
                                        <FiStar className="text-red-500 fill-current" size={12} />
                                        <span className="ml-1 text-sm font-semibold">{product.rating || '4.2'}</span>
                                        <span className="ml-2 text-[10px] text-blue-600 hover:underline cursor-pointer">
                                            ({product.reviewCount || '120'} ratings)
                                        </span>
                                    </div>
                                    <span className="text-gray-300">|</span>
                                    <span className="text-[10px] text-green-600 font-medium">In Stock</span>
                                </div>

                                <h3
                                    className="text-sm font-bold text-gray-900 mb-1.5 cursor-pointer hover:text-red-500 transition-colors line-clamp-2"
                                    onClick={handleProductClick}
                                >
                                    {product.name}
                                </h3>

                                {product.description && (
                                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{product.description}</p>
                                )}

                                {/* SIZE DISPLAY - Maximum 3 sizes */}
                                {productSizes.length > 0 && (
                                    <div className="mb-2 flex flex-wrap gap-1">
                                        {productSizes.map((size, index) => (
                                            <span
                                                key={index}
                                                className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-md border border-gray-200"
                                            >
                                                {size}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="w-40 flex-shrink-0">
                                <div className="mb-3 text-right">
                                    <div className="flex items-baseline justify-end gap-1.5">
                                        <span className="text-base font-bold text-gray-900">
                                            ₹{product.price || product.newPrice}
                                        </span>

                                        {(product.originalPrice || product.oldPrice) >
                                            (product.price || product.newPrice) && (
                                                <span className="text-sm text-gray-400 line-through">
                                                    ₹{Math.ceil(originalPrice)}
                                                </span>
                                            )}
                                    </div>

                                    {savings > 0 && (
                                        <span className="text-[9px] text-green-600 font-light ml-auto">
                                            Save ₹{savings}
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <button
                                        onClick={handleWishlistClick}
                                        className={`flex items-center justify-center gap-1.5 py-2 rounded-md border transition-all text-xs ${isWishlisted ? "border-red-400 text-red-400 bg-red-50" : "border-gray-300 text-gray-700 hover:border-red-400 hover:text-red-400 hover:bg-red-50"}`}
                                    >
                                        <FiHeart className={isWishlisted ? "fill-current" : ""} size={12} />
                                        {isWishlisted ? "Added" : "Wishlist"}
                                    </button>

                                    {cartQuantity > 0 ? (
                                        <div className="flex items-center justify-between bg-red-50 rounded-md p-1.5">
                                            <button onClick={handleDecrement} className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-100 text-red-700"><FiMinus size={12} /></button>
                                            <span className="font-bold text-red-700 text-sm">{cartQuantity}</span>
                                            <button onClick={handleIncrement} className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-100 text-red-700"><FiPlus size={12} /></button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleAddClick}
                                            disabled={isOutOfStock}
                                            className={`w-full py-2 rounded-md font-medium flex items-center justify-center gap-1.5 transition-all text-xs ${isOutOfStock ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-red-500 to-red-400 text-white hover:shadow hover:shadow-red-200"}`}
                                        >
                                            <FiShoppingCart size={12} />
                                            {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    // GRID VIEW (MOBILE & DESKTOP)
    return (
        <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full cursor-pointer"
            onClick={handleProductClick}
        >
            {/* SQUARE IMAGE CONTAINER (Grid View) */}
            <div className="relative w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                <img
                    src={imageError ? "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400" : (product.image || product.imageUrl)}
                    alt={product.name}
                    className="w-full h-full object-contain p-2"
                    onError={() => setImageError(true)}
                />

                {product.discount > 0 && (
                    <div
                        className="absolute top-1.5 left-1.5 bg-gradient-to-r from-red-500 to-red-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {product.discount}% OFF
                    </div>
                )}

                <div
                    className="absolute top-1.5 right-1.5 bg-white/95 backdrop-blur-sm text-gray-700 px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1 border border-gray-100"
                    onClick={(e) => e.stopPropagation()}
                >
                    <FiStar className="text-red-500 fill-current" size={10} />
                    <span className="text-[10px] font-bold">{product.rating || '4.2'}</span>
                </div>

                <button
                    onClick={handleWishlistClick}
                    className="absolute bottom-1.5 right-1.5 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm"
                >
                    <FiHeart className={isWishlisted ? "text-red-400 fill-current" : "text-gray-500 hover:text-red-400"} size={12} />
                </button>

                {isLowStock && !isOutOfStock && (
                    <div
                        className="absolute bottom-1.5 left-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Only {product.stock} left
                    </div>
                )}
            </div>

            {/* Product Info */}
            <div className="p-2" onClick={(e) => e.stopPropagation()}>
                <h4
                    className="text-xs font-bold text-gray-900 mb-1.5 line-clamp-2 h-8 hover:text-red-500 cursor-pointer"
                    onClick={handleProductClick}
                >
                    {product.name}
                </h4>

                <div className="flex items-baseline gap-1.5 mb-2">
                    <span className="text-sm font-bold text-gray-900">₹{product.price || product.newPrice}</span>
                    {(product.originalPrice || product.oldPrice) > (product.price || product.newPrice) && (
                        <span className="text-xs text-gray-400 line-through">₹{Math.ceil(originalPrice)}</span>
                    )}
                    {savings > 0 && (
                        <span className="text-[9px] text-green-600 font-light ml-auto">Save ₹{savings}</span>
                    )}
                </div>

                {cartQuantity > 0 ? (
                    <div className="flex items-center justify-between bg-red-50 rounded-md p-1">
                        <button onClick={handleDecrement} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 text-red-700"><FiMinus size={10} /></button>
                        <span className="font-medium text-red-700 text-xs">{cartQuantity}</span>
                        <button onClick={handleIncrement} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 text-red-700"><FiPlus size={10} /></button>
                    </div>
                ) : (
                    <button
                        onClick={handleAddClick}
                        disabled={isOutOfStock}
                        className={`w-full py-1.5 rounded-md font-medium text-xs flex items-center justify-center gap-1 ${isOutOfStock ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-red-500 to-red-400 text-white hover:shadow hover:shadow-red-200"}`}
                    >
                        <FiShoppingCart size={10} />
                        {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                    </button>
                )}
            </div>
        </motion.div>
    );
};

export default ProductCard;